import FiestaInfiniteSpinner from 'src/shared/components/infinite-spinner/FiestaInfiniteSpinner';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiestaReferralCard } from 'src/shared/components';
import AppFooter from 'src/core/components/app-footer/AppFooter';
import {
  ActionsService,
  AmbassadorRegistrationResponse,
  CReferralIdentifier,
  CReferralMode,
  CReferralStatus,
  PreCheckResultCode,
  ProgramsGetDetailResponse,
  ProgramsGetDetailResponse_CProgram,
  ProgramsService
} from 'src/apis/api-client';
import { Carousel } from 'primereact/carousel';
import { Button } from 'primereact/button';
import { track } from 'src/shared/utils/TrackingUtils';
import { SignOutButton } from 'src/shared';
import logo from 'src/assets/images/fiesta.png';

const IOS_URL = 'https://apps.apple.com/de/app/fiesta-club/id6751592725';
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=club.fiesta.app&pcampaignid=web_share';

const APPSTORE_ICON_KEY = 'domains.welcome.dialogs.mobile-app-welcome.appstore_icon';
const PLAYSTORE_ICON_KEY = 'domains.welcome.dialogs.mobile-app-welcome.playstore_icon';

function StoreBadges({ height = 48 }: { height?: number }) {
  return (
    <div className='flex flex-row gap-3 flex-wrap justify-content-center'>
      <a href={IOS_URL} target='_blank' rel='noreferrer'>
        <img src={t(APPSTORE_ICON_KEY)} alt='Download on the App Store' style={{ height }} />
      </a>
      <a href={ANDROID_URL} target='_blank' rel='noreferrer'>
        <img src={t(PLAYSTORE_ICON_KEY)} alt='Get it on Google Play' style={{ height }} />
      </a>
    </div>
  );
}

function StatusPill({ label, color }: { label: string; color: string }) {
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.4rem',
      padding: '0.3rem 0.85rem',
      borderRadius: '100px',
      background: `${color}18`,
      border: `1px solid ${color}55`,
      color,
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase' as const,
      marginBottom: '0.75rem',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
      {label}
    </div>
  );
}

export function JoinView() {
  const routeParams = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [program, setProgram] = useState<ProgramsGetDetailResponse_CProgram>();
  const [referrals, setReferrals] = useState<CReferralIdentifier[]>();
  const [waitlist, setWaitlist] = useState<boolean>(false);
  // 'app' = step 1 (pitch), 'code' = step 2 (reveal)
  const [step, setStep] = useState<'app' | 'code'>('app');

  useEffect(
    () => {
      (
        async () => {
          if (!routeParams.programId)
            return;

          track('WebApp_JoinView', [routeParams.programId]);
          setLoading(true);

          const preCheckResponse =
            await ActionsService.registerAmbassadorPrecheck(routeParams.programId)
              .catch(() => navigate('/app'));

          if (preCheckResponse && preCheckResponse.data) {
            if (preCheckResponse.data.registrationPossible) {
              const programResponse: ProgramsGetDetailResponse
                = await ProgramsService.getDetail(routeParams.programId);

              if (programResponse && programResponse.data) {
                setProgram(programResponse.data);

                if (programResponse.data.eventId && programResponse.data.id) {
                  const regResponse: AmbassadorRegistrationResponse =
                    await ActionsService.registerAmbassador(programResponse.data.id);

                  switch (regResponse.status) {
                    case CReferralStatus.REGISTERED:
                      if (programResponse.data.mode === CReferralMode.BASKET) {
                        navigate('/app');
                        return;
                      }
                      if (regResponse.data) {
                        const displayable = regResponse.data.filter(r => r.isDisplayable !== false);
                        setReferrals(displayable.length > 0 ? displayable : regResponse.data);
                      }
                      break;
                    case CReferralStatus.WAITLIST:
                      setWaitlist(true);
                      break;
                    default:
                      navigate('/app');
                  }
                }
              }
            } else {
              if (preCheckResponse.data.resultCode === PreCheckResultCode.ALREADY_REGISTERED) {
                navigate('/app');
              } else if (preCheckResponse.data.resultCode === PreCheckResultCode.WAITLIST) {
                setWaitlist(true);
              } else {
                navigate('/programs/sorry/' + preCheckResponse.data.resultCode);
              }
            }
          } else {
            navigate('/app');
          }

          setLoading(false);
        }
      )();
    },
    [routeParams.programId]
  );

  /** Step 1 — App pitch. "Your code is ready — but first, here's why you need the app." */
  function renderAppStep() {
    return (
      <div
        className='flex flex-column align-items-center text-center'
        style={{ width: '100%', maxWidth: '520px', gap: '0' }}
      >
        <img src={logo} alt='FIESTA' style={{ width: '100px', marginBottom: '1.25rem' }} />

        <StatusPill label={t('domains.programs.views.join.joinView.appMessage.registrationPill')} color='#64E800' />

        <h1
          className='font-bebas'
          style={{ fontSize: 'clamp(2rem, 5vw, 2.8rem)', color: '#DBF3F7', margin: '0 0 0.75rem 0' }}
        >
          {t('domains.programs.views.join.joinView.appMessage.headline')}
        </h1>

        <p style={{ color: 'rgba(219, 243, 247, 0.7)', fontSize: '1rem', lineHeight: '1.6', margin: '0 0 1.5rem 0' }}>
          {t('domains.programs.views.join.joinView.appMessage.lossText')}
        </p>

        {/* Feature bullets */}
        <div style={{ width: '100%', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.75rem' }}>
          {(['feature1', 'feature2', 'feature3', 'feature4'] as const).map((key) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', color: '#DBF3F7', fontSize: '0.95rem' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#64E800', flexShrink: 0, boxShadow: '0 0 6px rgba(100,232,0,0.5)' }} />
              {t(`domains.programs.views.join.joinView.appMessage.${key}`)}
            </div>
          ))}
        </div>

        {/* Primary CTA — reveals the code */}
        <Button
          label={t('domains.programs.views.join.joinView.appMessage.revealButton')}
          onClick={() => setStep('code')}
          className='btn-reveal'
          style={{
            width: '100%',
            background: '#DBF227',
            color: '#E5006E',
            border: 'none',
            fontWeight: 700,
            fontSize: '1.05rem',
            padding: '0.9rem 1.5rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            cursor: 'pointer',
          }}
        />

        {/* Secondary — store badges */}
        <StoreBadges height={44} />
      </div>
    );
  }

  /** Step 2 — Code reveal. Full focus on the code, quiet store badges below. */
  function renderCodeStep() {
    return (
      <div
        className='flex flex-column align-items-center text-center'
        style={{ width: '100%', maxWidth: '520px', gap: '0' }}
      >
        <img src={logo} alt='FIESTA' style={{ width: '100px', marginBottom: '1.25rem' }} />

        <h1
          className='font-bebas'
          style={{ fontSize: 'clamp(2rem, 5vw, 2.8rem)', color: '#DBF3F7', margin: '0 0 0.5rem 0' }}
        >
          {t('domains.programs.views.join.joinView.referralMessage.header')}
        </h1>

        <p style={{ color: 'rgba(219, 243, 247, 0.6)', fontSize: '0.9rem', margin: '0 0 1.5rem 0' }}>
          {t('domains.programs.views.join.joinView.referralMessage.copyPrompt')}
        </p>

        {/* Code card — prominent */}
        <div style={{
          width: '100%',
          background: 'rgba(10, 22, 40, 0.7)',
          border: '1px solid rgba(219, 242, 39, 0.3)',
          borderRadius: '16px',
          padding: '0.25rem',
          marginBottom: '1.75rem',
          boxShadow: '0 0 40px rgba(219, 242, 39, 0.06)',
        }}>
          {referrals && (
            <Carousel
              className='w-full'
              value={referrals}
              showIndicators={false}
              itemTemplate={(referral: CReferralIdentifier) => <FiestaReferralCard data={referral} />}
              showNavigators={referrals.length > 1}
            />
          )}
        </div>

        {/* Quiet store badges */}
        <StoreBadges height={40} />

        <SignOutButton
          loginType='redirect'
          label={t('domains.programs.views.join.joinView.signOut')}
          className='p-button-text p-button-secondary'
          style={{ marginTop: '1.5rem', color: 'rgba(219, 243, 247, 0.35)', fontSize: '0.85rem' }}
        />

        <div
          className='flex flex-column align-items-center gap-2'
          style={{ marginTop: '2.5rem', opacity: 0.45 }}
        >
          <AppFooter showLogo={false} />
        </div>
      </div>
    );
  }

  /** Waitlist — single screen. App is framed as the delivery channel for the code. */
  function renderWaitlistScreen() {
    return (
      <div
        className='flex flex-column align-items-center text-center'
        style={{ width: '100%', maxWidth: '520px', gap: '0' }}
      >
        <img src={logo} alt='FIESTA' style={{ width: '100px', marginBottom: '1.25rem' }} />

        <StatusPill label={t('domains.programs.views.join.joinView.waitlistMessage.statusPill')} color='#DBF227' />

        <h1
          className='font-bebas'
          style={{ fontSize: 'clamp(2rem, 5vw, 2.8rem)', color: '#DBF3F7', margin: '0 0 0.75rem 0' }}
        >
          {t('domains.programs.views.join.joinView.waitlistMessage.reservedHeader')}
        </h1>

        <p style={{ color: 'rgba(219, 243, 247, 0.75)', fontSize: '1rem', lineHeight: '1.6', margin: '0 0 1.75rem 0' }}>
          {t('domains.programs.views.join.joinView.waitlistMessage.textContent')}
        </p>

        {/* App CTA card — Lime accent */}
        <div style={{
          width: '100%',
          background: 'rgba(100, 232, 0, 0.06)',
          border: '1.5px solid rgba(100, 232, 0, 0.28)',
          borderRadius: '16px',
          padding: '1.5rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1.1rem',
        }}>
          <div>
            <p
              className='font-bebas'
              style={{ fontSize: '1.65rem', color: '#64E800', margin: '0 0 0.35rem 0', letterSpacing: '0.05em' }}
            >
              {t('domains.programs.views.join.joinView.waitlistMessage.appCtaHeadline')}
            </p>
            <p style={{ color: 'rgba(219, 243, 247, 0.65)', fontSize: '0.875rem', lineHeight: '1.55', margin: 0 }}>
              {t('domains.programs.views.join.joinView.waitlistMessage.appCtaSubtext')}
            </p>
          </div>

          <div style={{ width: '100%', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
            {(['feature1', 'feature2', 'feature3'] as const).map((key) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', color: '#DBF3F7', fontSize: '0.9rem' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#64E800', flexShrink: 0 }} />
                {t(`domains.programs.views.join.joinView.waitlistMessage.${key}`)}
              </div>
            ))}
          </div>

          <StoreBadges height={48} />
        </div>

        <SignOutButton
          loginType='redirect'
          label={t('domains.programs.views.join.joinView.signOut')}
          className='p-button-text p-button-secondary'
          style={{ marginTop: '1.5rem', color: 'rgba(219, 243, 247, 0.35)', fontSize: '0.85rem' }}
        />

        <div
          className='flex flex-column align-items-center gap-2'
          style={{ marginTop: '2.5rem', opacity: 0.45 }}
        >
          <AppFooter showLogo={false} />
        </div>
      </div>
    );
  }

  return (
    <div
      className='flex flex-column align-items-center justify-content-center'
      style={{ minHeight: '100vh', padding: '2rem' }}
    >
      {loading && <FiestaInfiniteSpinner />}
      {!loading && referrals != null && step === 'app'  && renderAppStep()}
      {!loading && referrals != null && step === 'code' && renderCodeStep()}
      {!loading && waitlist && renderWaitlistScreen()}
    </div>
  );
}
